/**
 * endingsGallery — pure data layer for the Endings Gallery screen (MY-1988).
 *
 * No Phaser import — fully testable in Node.
 * Builds display-ready gallery entries from ending definitions + save data.
 */

import { ENDINGS } from '@/data/endings';
import type { EndingDef } from '@/data/endings';
import { TOTAL_ENDINGS } from '@/systems/rewardTiers';

export interface GalleryEntry {
  /** Full ending definition. */
  ending: EndingDef;
  /** True when this ending id appears in SaveSlotData.endingsSeen. */
  seen: boolean;
  /** 1-based display index shown on locked silhouette cards. */
  displayIndex: number;
}

export interface GalleryCounter {
  seen: number;
  total: number;
}

/**
 * Build the 14-entry gallery from the canonical ENDINGS list and the player's
 * seen-ids array (from SaveSlotData.endingsSeen). Preserves canonical order.
 */
export function buildGalleryEntries(seenIds: readonly string[]): GalleryEntry[] {
  const seenSet = new Set(seenIds);
  return ENDINGS.map((ending, i) => ({
    ending,
    seen: seenSet.has(ending.id),
    displayIndex: i + 1,
  }));
}

/** Returns the "X/14 discovered" counter from a built gallery. */
export function galleryCounter(entries: readonly GalleryEntry[]): GalleryCounter {
  return {
    seen: entries.filter((e) => e.seen).length,
    total: TOTAL_ENDINGS,
  };
}

/**
 * Hint text shown beneath the ??? label on an unseen card.
 * Gated endings surface their lockHint; open endings use a generic phrase.
 */
export function lockedCardHint(entry: GalleryEntry): string {
  if (entry.seen) return '';
  return entry.ending.lockHint !== '' ? entry.ending.lockHint : 'Yet to be discovered';
}
